import { X, Check } from "lucide-react";
import { Reveal } from "./motion";

const isNot = [
  "A script to memorize",
  "A polished introduction",
  "A race to explain what you do",
  "A way to collect as many contacts as possible",
  "A shortcut to an immediate sale",
];

const is = [
  "A simple, warm greeting",
  "Genuine curiosity",
  "Intentional listening",
  "A chance to serve",
  "A path to a deeper conversation",
];

export const NotElevatorPitch = () => {
  return (
    <section className="border-b-2 border-ink bg-white py-20 md:py-28" data-testid="not-elevator-pitch-section">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <Reveal>
          <p className="mb-5 font-mono text-xs font-bold uppercase tracking-[0.22em] text-ink/50">
            Read this part first
          </p>
          <h2 className="max-w-3xl font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            This is not an elevator pitch.
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-8 grid max-w-4xl grid-cols-1 gap-5 text-lg leading-relaxed text-ink/70 md:text-xl">
            <p>
              An elevator pitch makes the conversation about you before you know anything about them.
              It turns a human interaction into a performance.
            </p>
            <p className="font-semibold text-ink">
              Intentional networking starts differently: begin warmly, make the conversation about the
              other person, and listen.
            </p>
          </div>
        </Reveal>

        {/* Comparison */}
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
          <Reveal delay={0.05}>
            <div className="h-full border-2 border-ink bg-cloud p-7 md:p-8" data-testid="what-this-is-not">
              <p className="mb-6 font-mono text-xs font-bold uppercase tracking-[0.18em] text-ink/50">
                What this is not
              </p>
              <ul className="space-y-4">
                {isNot.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-base font-medium text-ink md:text-lg">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center border-2 border-ink bg-white">
                      <X className="h-3.5 w-3.5" strokeWidth={3} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="h-full border-2 border-ink bg-ink p-7 text-white md:p-8" data-testid="what-this-is">
              <p className="mb-6 font-mono text-xs font-bold uppercase tracking-[0.18em] text-cyan">
                What this is
              </p>
              <ul className="space-y-4">
                {is.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-base font-medium text-white md:text-lg">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center border-2 border-ink bg-acid text-ink">
                      <Check className="h-3.5 w-3.5" strokeWidth={3} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        {/* Why this works */}
        <Reveal delay={0.1}>
          <div className="mt-6 border-2 border-ink bg-white p-7 md:p-10">
            <p className="mb-4 font-mono text-xs font-bold uppercase tracking-[0.18em] text-ink/50">
              Why this works
            </p>
            <p className="max-w-4xl text-lg leading-relaxed text-ink md:text-xl">
              People remember the conversations where they felt seen, heard, and valued. None of that
              happens while you are reciting a pitch. It happens while you are listening.
            </p>
            <p className="mt-5 max-w-4xl text-base leading-relaxed text-ink/60 md:text-lg">
              The goal is not to teach you how to perform better. The goal is to help you trust yourself
              enough to have a real conversation.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
